<script setup lang="ts">
  import { onMounted, ref, computed } from 'vue'
  import {
    PhoneNumbersService,
    OrganisationsService,
    RetellAgentsService,
    type SuperUserPhoneNumber as PhoneNumber,
    type SuperUserRetellAgent as RetellAgent,
    type SuperUserOrganisation as Organisation
  } from '@/types/generated'
  import { useRoute } from 'vue-router'
  import { ROUTES } from '@/router/routes'
  import Breadcrumbs from '@/components/admin/breadcrumbs/Breadcrumbs.vue'
  import * as yup from 'yup'
  import type { GenericObject } from 'vee-validate'
  import { type Option } from '@/components/admin/form/SelectField.vue'
  import { useTranslation } from '@/composables/useTranslation'
  import SelectField from '@/components/admin/form/SelectField.vue'
  import AutoSavingForm from '@/components/admin/form/AutoSavingForm.vue'
  import CheckboxField from '@/components/admin/form/CheckboxField.vue'
  import { PhoneIcon, ClipboardIcon, CircleHelp } from 'lucide-vue-next'
  import InputField from '@/components/admin/form/InputField.vue'
  import TextAreaField from '@/components/admin/form/TextAreaField.vue'
  import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'

  const { t } = useTranslation({
    en: {
      active: 'Active',
      retellAgent: 'Retell agent',
      customer: 'Customer',
      phoneNumber: 'Phone number',
      copy: 'Copy',
      deactivationWarning:
        'Warning: Deactivating this phone number will deregister it from the phone provider. It will no longer be available for incoming calls. This action can not be undone.',
      inactiveInfo:
        'This phone number is inactive. It can not be reactivated, please create a new phone number for the customer.',
      description: 'Description',
      descriptionPlaceholder: 'e.g. standard AI assistant for general calls',
      lawFirmName: 'Law Firm Name',
      lawFirmNamePlaceholder: 'e.g. Müller & Associates Law Firm',
      originalPhoneNumberPlaceholder: 'Your own law firm number, e.g. +49555555555555',
      address: 'Address',
      addressPlaceholder: 'e.g. Musterstraße 1\n12345 Musterstadt',
      opening_hours: 'Opening Hours',
      openingHoursPlaceholder: 'e.g. Mondays to Fridays, from 9 am to 4 pm',
      lawyer_names: 'Lawyer Names',
      lawyerNamesPlaceholder: 'e.g. Max Müller\nLisa Müller',
      has_free_initial_consultation: 'Has free initial consultation',
      developer: 'Developer',
      begin_message: 'Welcome message',
      beginMessagePlaceholder:
        "current message:\nHello, my name is Marie and I am the AI call answering service for {'{'} law firm name {'}'}. I will be happy to take your enquiry and pass it on to our team immediately so that we can help you as quickly as possible. You can speak to me in full sentences, just as you would to a human being. How can we help you?",
      forwarding_no_answer_message: 'Message for unanswered forwarded calls',
      forwardingNoAnswerMessagePlaceholder:
        'current message: Unfortunately, the line is currently busy. I will be happy to take your request immediately and you will receive a response as soon as possible.',
      end_message: 'Goodbye message',
      endMessagePlaceholder:
        'The current message depends on the conversation content.\ne.g. Thank you for your call. Your request will be processed as quickly as possible.',
      descriptionHint: 'Here you can store an internal designation for the phone number.',
      lawFirmNameHint:
        'Here you specify the name of the law firm as it should be mentioned in the greeting announcement.',
      originalPhoneNumberHint:
        'If you forward our generated number internally, please enter the original number here that clients call. This ensures we can correctly assign calls.',
      freeConsultationHint:
        'If a free initial consultation is offered, this should be activated here. The AI can then point out that the initial consultation is free.',
      addressHint:
        'Here you can specify the address of the law firm. We will pass this on to the caller upon request. If you do not want unannounced client visits, please provide a corresponding note here.',
      openingHoursHint:
        'Here you can enter the opening hours. These can be reproduced by the AI upon request.',
      lawyerNamesHint:
        "Here the names of the lawyers are entered. This helps the AI to better recognize them and distinguish them from the caller's name. Only enter the name here and no other designation such as lawyer or case worker.",
      beginMessageHint:
        'Here you can store a welcome message for the law firm. This will be played to the caller in advance before the call is answered.',
      forwardingNoAnswerMessageHint:
        'This message is played when a forwarded call goes unanswered.',
      endMessageHint:
        'Here you can store a farewell message for the law firm. This will be played to the caller at the end of the call after all information has been collected.'
    },
    de: {
      active: 'Aktiv',
      retellAgent: 'Retell Agent',
      customer: 'Kunde',
      phoneNumber: 'Telefonnummer',
      copy: 'Kopieren',
      deactivationWarning:
        'Warnung: Durch das Deaktivieren dieser Telefonnummer wird sie vom Telefonanbieter abgemeldet. Sie ist nicht mehr für eingehende Anrufe verfügbar. Diese Aktion kann nicht rückgängig gemacht werden.',
      inactiveInfo:
        'Diese Telefonnummer ist inaktiv. Sie kann nicht reaktiviert werden, bitte eine neue Telefonnummer für den Kunden erstellen.',
      description: 'Beschreibung',
      descriptionPlaceholder: 'z.B. Standard-KI-Assistent für generelle Anrufe',
      lawFirmName: 'Kanzleiname',
      lawFirmNamePlaceholder: 'z.B. Anwaltskanzlei Müller & Partner',
      originalPhoneNumberPlaceholder: 'Ihre eigene Kanzleitelefonnummer, z.B. +49555555555555',
      address: 'Adresse',
      addressPlaceholder: 'z.B. Musterstraße 1\n12345 Musterstadt',
      opening_hours: 'Öffnungszeiten',
      openingHoursPlaceholder: 'z.B. Montags bis Freitags, von 9.00 Uhr bis 16.00 Uhr',
      lawyer_names: 'Anwälte',
      lawyerNamesPlaceholder: 'z.B. Max Müller\nLisa Müller',
      has_free_initial_consultation: 'Erstberatung',
      developer: 'Entwickler',
      begin_message: 'Begrüßungsnachricht',
      beginMessagePlaceholder:
        "aktuelle Nachricht: Hallo, mein Name ist Marie und ich bin die KI Anrufannahme der {'{'}Kanzleiname{'}'}. Ich nehme gerne Ihr Anliegen auf und gebe es sofort an unser Team weiter, damit wir Ihnen schnellstmöglich weiterhelfen können. Sie können mit mir in ganzen Sätzen wie mit einem Menschen sprechen. Wobei können wir behilflich sein?",
      forwarding_no_answer_message: 'Nachricht bei nicht beantworteter Weiterleitung',
      forwardingNoAnswerMessagePlaceholder:
        'aktuelle Nachricht: Leider ist die Leitung aktuell belegt. Ich nehme gerne Ihr Anliegen sofort auf und Sie erhalten schnellstmöglich eine Rückmeldung.',
      end_message: 'Abschiedsnachricht',
      endMessagePlaceholder:
        'Die aktuelle Nachricht hängt vom Gespräch ab.\nz.B. Vielen Dank für Ihren Anruf, Ihr Anliegen wird schnellstmöglich bearbeitet.',
      descriptionHint: 'Hier kann eine interne Bezeichnung für die Rufnummer hinterlegt werden.',
      lawFirmNameHint:
        'Hier wird der Name der Kanzlei angegeben, wie er in der Begrüßungsansage genannt werden soll.',
      originalPhoneNumberHint:
        'Wenn Sie die von uns generierte Nummer intern weiterleiten, geben Sie hier unbedingt die Originalnummer an, welche von den Mandanten angerufen wird. So stellen Sie sicher, dass wir die Anrufe korrekt zuordnen können.',
      freeConsultationHint:
        'Wenn eine kostenlose Erstberatung angeboten wird, sollte das hier aktiviert werden. Die KI kann dann darauf hinweisen, dass die Erstberatung kostenlos ist.',
      addressHint:
        'Hier kann die Adresse der Kanzlei angegeben werden. Bei Nachfrage geben wir diese an den Anrufer weiter. Wenn Sie keine unangekündigten Mandantenbesuch wünschen, dann geben Sie hier bitte einen entsprechenden Hinweis.',
      openingHoursHint:
        'Hier können die Öffnungszeiten eingetragen werden. Diese können bei Nachfrage von der KI-Wiedergegeben werden.',
      lawyerNamesHint:
        'Hier werden die Namen der Anwälte eingetragen. Damit die KI diese besser erkennt und vom Anrufernamen unterscheiden kann. Hier nur den Namen eintragen und keine weitere Bezeichnung wie zum Beispiel Anwalt oder Sachbearbeiter.',
      beginMessageHint:
        'Hier kann eine Begrüßungsnachricht für die Kanzlei hinterlegt werden. Diese wird vorab an den Anrufer gespielt, bevor der Anruf angenommen wird.',
      forwardingNoAnswerMessageHint:
        'Diese Nachricht wird abgespielt, wenn eine weitergeleitete Nummer nicht abnimmt.',
      endMessageHint:
        'Hier kann eine Abschiedsnachricht für die Kanzlei hinterlegt werden. Diese wird am Schluss des Anrufes abgespielt, nachdem alle Informationen gesammelt wurden.'
    }
  })

  const isDev = import.meta.env.VITE_ENV === 'development'

  const LANGUAGE_STYLES: Record<string, { label: string; classes: string }> = {
    de: { label: 'DE', classes: 'bg-purple-50 text-purple-700 border border-purple-200' },
    en: { label: 'EN', classes: 'bg-emerald-50 text-emerald-700 border border-emerald-200' },
    fr: { label: 'FR', classes: 'bg-violet-50 text-violet-700 border border-violet-200' },
    pl: { label: 'PL', classes: 'bg-blue-50 text-blue-700 border border-blue-200' }
  }

  const route = useRoute()
  const phoneNumber = ref<PhoneNumber | null>(null)
  const defaultBeginMessage = computed(() => {
    return `Hallo, mein Name ist Marie und ich bin die KI Anrufannahme der ${phoneNumber.value.lawfirm_name}. Ich nehme gerne Ihr Anliegen auf und gebe es sofort an unser Team weiter, damit wir Ihnen schnellstmöglich weiterhelfen können.
Sie können mit mir in ganzen Sätzen wie mit einem Menschen sprechen.
Wobei können wir behilflich sein ?
    `
  })

  const defaultForwardingNoAnswerMessage =
    'Leider ist die Leitung aktuell belegt. Ich nehme aber gerne Ihr Anliegen sofort auf und Sie erhalten schnellstmöglich eine Rückmeldung.'

  const retellAgentsList = ref<RetellAgent[] | null>(null)

  const parseDeveloper = (name: string | null | undefined): string => {
    if (!name) return 'Other'
    if (name.startsWith('Staging')) return 'Staging'
    const match = name.match(/^(.+?)(?:'s\s|'\s| Local Dev| Testing Agent| Voice)/)
    if (match) return match[1].trim()
    return 'Other'
  }

  const selectedDeveloper = ref<string | null>(null)

  const developerNames = computed<string[]>(() => {
    if (!isDev || !retellAgentsList.value) return []
    const devs = new Set<string>()
    retellAgentsList.value.forEach((agent) => {
      devs.add(parseDeveloper(agent.agent_name))
    })
    return [...devs].sort()
  })

  const filteredAgents = computed<RetellAgent[]>(() => {
    if (!retellAgentsList.value) return []
    if (!isDev || !selectedDeveloper.value) return retellAgentsList.value
    return retellAgentsList.value.filter(
      (agent) => parseDeveloper(agent.agent_name) === selectedDeveloper.value
    )
  })

  const agentByUuid = computed<Record<string, RetellAgent>>(() => {
    const map: Record<string, RetellAgent> = {}
    retellAgentsList.value?.forEach((agent) => {
      map[agent.uuid] = agent
    })
    return map
  })

  const retellAgentOptions = computed<Option[]>(() => {
    return filteredAgents.value.map((agent) => ({
      value: agent.uuid,
      title: agent.agent_name ?? agent.retell_agent_id
    }))
  })

  const getLanguageBadges = (uuid: string) => {
    const agent = agentByUuid.value[uuid]
    if (!agent) return []
    const name = agent.agent_name ?? ''
    const multiMatch = name.match(/\(([A-Z]{2}(?:\s*\+\s*[A-Z]{2})+)\)/)
    if (multiMatch) {
      return multiMatch[1]
        .split('+')
        .map((c) => c.trim().toLowerCase())
        .filter((c) => LANGUAGE_STYLES[c])
    }
    if (agent.language && LANGUAGE_STYLES[agent.language]) {
      return [agent.language]
    }
    return []
  }

  const organisationsList = ref<Organisation[] | null>(null)
  const organisationOptions = computed<Option[]>(() => {
    return (
      organisationsList.value?.map((organisation) => {
        return {
          value: organisation.uuid,
          title: organisation.name
        }
      }) ?? []
    )
  })

  onMounted(async () => {
    phoneNumber.value = (await PhoneNumbersService.phoneNumbersRetrieve({
      uuid: route.params.uuid.toString()
    })) as PhoneNumber
    const agentsResp = await RetellAgentsService.retellAgentsList()
    retellAgentsList.value = (agentsResp.results ?? agentsResp) as unknown as RetellAgent[]
    const orgsResp = await OrganisationsService.organisationsList()
    organisationsList.value = (orgsResp.results ?? orgsResp) as unknown as Organisation[]
  })

  const handleSave = async (values: GenericObject) => {
    await PhoneNumbersService.phoneNumbersPartialUpdate({
      uuid: phoneNumber.value!.uuid!,
      requestBody: values
    })
  }

  const copyPhoneNumberToClipboard = () => {
    navigator.clipboard.writeText(phoneNumber.value!.phone_number as string)
  }
</script>
<template>
  <TooltipProvider>
    <div v-if="phoneNumber">
      <div class="space-y-5 p-2 sm:p-5 sm:py-0 md:pt-5">
        <!-- Page Header -->
        <div class="flex items-center justify-between gap-x-5">
          <Breadcrumbs
            :items="[
              { title: 'Dashboard', to: ROUTES.ADMIN.DASHBOARD },
              { title: 'Phone numbers', to: ROUTES.ADMIN.VOICE.PHONE_NUMBER_LIST },
              {
                title: `Details - ${phoneNumber.uuid}`,
                to: ROUTES.ADMIN.VOICE.CALL_DETAIL.replace(':uuid', phoneNumber.uuid)
              }
            ]"
          />
          <div class="flex items-center justify-end gap-x-2"></div>
        </div>
        <div
          class="flex flex-col rounded-xl border border-gray-200 bg-white p-5 shadow-sm dark:border-neutral-700
            dark:bg-neutral-800"
        >
          <div class="w-full md:w-1/2">
            <AutoSavingForm @save="handleSave">
              <span
                v-if="phoneNumber.active"
                class="text-sm text-yellow-600"
              >
                {{ t('deactivationWarning') }}
              </span>
              <span
                v-else
                class="text-sm text-red-600"
              >
                {{ t('inactiveInfo') }}
              </span>
              <CheckboxField
                v-model="phoneNumber.active"
                :label="t('active')"
                name="active"
                :rules="yup.boolean().oneOf([true, false])"
                :disabled="!phoneNumber.active"
              />
              <label class="mb-2 block text-sm font-medium">{{ t('phoneNumber') }}</label>
              <div class="relative flex rounded-lg shadow-sm">
                <input
                  type="text"
                  :value="phoneNumber.phone_number"
                  class="block w-full rounded-s-lg border-gray-200 bg-gray-100 px-4 py-3 ps-11 text-sm shadow-sm focus:z-10
                    focus:border-purple-500 focus:ring-purple-500 disabled:pointer-events-none disabled:opacity-50
                    dark:border-neutral-700 dark:bg-neutral-900 dark:text-neutral-400 dark:placeholder-neutral-500
                    dark:focus:ring-neutral-600"
                  disabled
                />
                <div
                  class="pointer-events-none absolute inset-y-0 start-0 z-20 flex items-center ps-4"
                >
                  <PhoneIcon class="size-4 shrink-0 text-gray-400 dark:text-neutral-500" />
                </div>
                <button
                  type="button"
                  class="inline-flex items-center justify-center gap-x-2 rounded-e-md border border-transparent bg-purple-600
                    px-4 py-3 text-sm font-semibold text-white hover:bg-purple-700 focus:bg-purple-700
                    focus:outline-none disabled:pointer-events-none disabled:opacity-50"
                  @click="copyPhoneNumberToClipboard"
                >
                  <ClipboardIcon class="size-4 shrink-0 text-white" />
                  {{ t('copy') }}
                </button>
              </div>
              <SelectField
                v-model="phoneNumber.org.uuid"
                :label="t('customer')"
                :is-filterable="true"
                name="org_uuid"
                :rules="yup.string().uuid().required()"
                :options="organisationOptions"
              />

              <!-- Developer filter (dev environment only) -->
              <div
                v-if="isDev && developerNames.length > 0"
                class="mt-4"
              >
                <label class="mb-2 block text-sm font-medium text-gray-700 dark:text-neutral-300">
                  {{ t('developer') }}
                </label>
                <div class="flex flex-wrap gap-2">
                  <button
                    v-for="dev in developerNames"
                    :key="dev"
                    type="button"
                    class="inline-flex items-center rounded-full px-3 py-1.5 text-sm font-medium transition-colors"
                    :class="
                      selectedDeveloper === dev
                        ? 'bg-purple-600 text-white shadow-sm'
                        : `bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-neutral-700 dark:text-neutral-300
                          dark:hover:bg-neutral-600`
                    "
                    @click="selectedDeveloper = selectedDeveloper === dev ? null : dev"
                  >
                    {{ dev }}
                    <span
                      v-if="selectedDeveloper === dev"
                      class="ml-1.5 text-xs opacity-75"
                    >
                      &times;
                    </span>
                  </button>
                </div>
              </div>

              <SelectField
                v-model="phoneNumber.agent.uuid"
                :label="t('retellAgent')"
                :is-filterable="true"
                name="agent_uuid"
                :rules="yup.string().uuid().required()"
                :options="retellAgentOptions"
              >
                <template #option-contents="{ option }">
                  <div class="flex w-full items-center justify-between">
                    <div class="flex items-center gap-2">
                      <span class="text-gray-800">{{ option.title }}</span>
                      <span
                        v-for="lang in getLanguageBadges(option.value as string)"
                        :key="lang"
                        class="inline-flex items-center rounded px-1.5 py-0.5 text-[10px] font-semibold leading-none"
                        :class="LANGUAGE_STYLES[lang]?.classes"
                      >
                        {{ LANGUAGE_STYLES[lang]?.label }}
                      </span>
                    </div>
                  </div>
                </template>
              </SelectField>
              <div class="space-y-1">
                <div class="flex items-center gap-2">
                  <label class="text-sm font-medium text-gray-900">{{ t('description') }}</label>
                  <Tooltip>
                    <TooltipTrigger>
                      <CircleHelp class="size-4 text-gray-400" />
                    </TooltipTrigger>
                    <TooltipContent
                      variant="secondary"
                      showArrow
                      class="max-w-xs"
                    >
                      <p>{{ t('descriptionHint') }}</p>
                    </TooltipContent>
                  </Tooltip>
                </div>
                <InputField
                  v-model="phoneNumber.description"
                  name="description"
                  :rules="yup.string()"
                  :placeholder="t('descriptionPlaceholder')"
                />
              </div>
              <div class="space-y-1">
                <div class="flex items-center gap-2">
                  <label class="text-sm font-medium text-gray-900">{{ t('lawFirmName') }}</label>
                  <Tooltip>
                    <TooltipTrigger>
                      <CircleHelp class="size-4 text-gray-400" />
                    </TooltipTrigger>
                    <TooltipContent
                      variant="secondary"
                      showArrow
                      class="max-w-xs"
                    >
                      <p>{{ t('lawFirmNameHint') }}</p>
                    </TooltipContent>
                  </Tooltip>
                </div>
                <InputField
                  v-model="phoneNumber.lawfirm_name"
                  name="lawfirm_name"
                  :rules="yup.string()"
                  :placeholder="t('lawFirmNamePlaceholder')"
                />
              </div>
              <div class="space-y-1">
                <div class="flex items-center gap-2">
                  <label class="text-sm font-medium text-gray-900">
                    Original {{ t('phoneNumber') }}
                  </label>
                  <Tooltip>
                    <TooltipTrigger>
                      <CircleHelp class="size-4 text-gray-400" />
                    </TooltipTrigger>
                    <TooltipContent
                      variant="secondary"
                      showArrow
                      class="max-w-xs"
                    >
                      <p>{{ t('originalPhoneNumberHint') }}</p>
                    </TooltipContent>
                  </Tooltip>
                </div>
                <InputField
                  v-model="phoneNumber.original_phone_number"
                  name="original_phone_number"
                  :rules="yup.string()"
                  :placeholder="t('originalPhoneNumberPlaceholder')"
                />
              </div>
              <div class="rounded-lg bg-gray-50 p-3">
                <div class="flex items-start gap-3">
                  <CheckboxField
                    v-model="phoneNumber.has_free_initial_consultation"
                    :label="t('has_free_initial_consultation')"
                    name="has_free_initial_consultation"
                  />
                  <div class="flex-1">
                    <p class="text-sm text-gray-600">{{ t('freeConsultationHint') }}</p>
                  </div>
                </div>
              </div>
              <div class="space-y-1">
                <div class="flex items-center gap-2">
                  <label class="text-sm font-medium text-gray-900">{{ t('address') }}</label>
                  <Tooltip>
                    <TooltipTrigger>
                      <CircleHelp class="size-4 text-gray-400" />
                    </TooltipTrigger>
                    <TooltipContent
                      variant="secondary"
                      showArrow
                      class="max-w-xs"
                    >
                      <p>{{ t('addressHint') }}</p>
                    </TooltipContent>
                  </Tooltip>
                </div>
                <TextAreaField
                  v-model="phoneNumber.address"
                  name="address"
                  :rules="yup.string()"
                  :placeholder="t('addressPlaceholder')"
                />
              </div>
              <div class="space-y-1">
                <div class="flex items-center gap-2">
                  <label class="text-sm font-medium text-gray-900">{{ t('opening_hours') }}</label>
                  <Tooltip>
                    <TooltipTrigger>
                      <CircleHelp class="size-4 text-gray-400" />
                    </TooltipTrigger>
                    <TooltipContent
                      variant="secondary"
                      showArrow
                      class="max-w-xs"
                    >
                      <p>{{ t('openingHoursHint') }}</p>
                    </TooltipContent>
                  </Tooltip>
                </div>
                <TextAreaField
                  v-model="phoneNumber.opening_hours"
                  name="opening_hours"
                  :rules="yup.string()"
                  :placeholder="t('openingHoursPlaceholder')"
                />
              </div>
              <div class="space-y-1">
                <div class="flex items-center gap-2">
                  <label class="text-sm font-medium text-gray-900">{{ t('lawyer_names') }}</label>
                  <Tooltip>
                    <TooltipTrigger>
                      <CircleHelp class="size-4 text-gray-400" />
                    </TooltipTrigger>
                    <TooltipContent
                      variant="secondary"
                      showArrow
                      class="max-w-xs"
                    >
                      <p>{{ t('lawyerNamesHint') }}</p>
                    </TooltipContent>
                  </Tooltip>
                </div>
                <TextAreaField
                  v-model="phoneNumber.lawyer_names"
                  name="lawyer_names"
                  :rules="yup.string()"
                  :placeholder="t('lawyerNamesPlaceholder')"
                />
              </div>
              <div class="space-y-1">
                <div class="flex items-center gap-2">
                  <label class="text-sm font-medium text-gray-900">{{ t('begin_message') }}</label>
                  <Tooltip>
                    <TooltipTrigger>
                      <CircleHelp class="size-4 text-gray-400" />
                    </TooltipTrigger>
                    <TooltipContent
                      variant="secondary"
                      showArrow
                      class="max-w-xs"
                    >
                      <p>{{ t('beginMessageHint') }}</p>
                    </TooltipContent>
                  </Tooltip>
                </div>
                <TextAreaField
                  v-model="phoneNumber.begin_message"
                  :value="phoneNumber.begin_message ?? defaultBeginMessage"
                  name="begin_message"
                  :rules="yup.string()"
                  :placeholder="t('beginMessagePlaceholder')"
                  rows="6"
                />
              </div>
              <div class="space-y-1">
                <div class="flex items-center gap-2">
                  <label class="text-sm font-medium text-gray-900">
                    {{ t('forwarding_no_answer_message') }}
                  </label>
                  <Tooltip>
                    <TooltipTrigger>
                      <CircleHelp class="size-4 text-gray-400" />
                    </TooltipTrigger>
                    <TooltipContent
                      variant="secondary"
                      showArrow
                      class="max-w-xs"
                    >
                      <p>{{ t('forwardingNoAnswerMessageHint') }}</p>
                    </TooltipContent>
                  </Tooltip>
                </div>
                <TextAreaField
                  v-model="phoneNumber.forwarding_no_answer_message"
                  :value="phoneNumber.forwarding_no_answer_message ?? ''"
                  name="forwarding_no_answer_message"
                  :rules="yup.string()"
                  :placeholder="t('forwardingNoAnswerMessagePlaceholder')"
                  rows="5"
                />
              </div>
              <div class="space-y-1">
                <div class="flex items-center gap-2">
                  <label class="text-sm font-medium text-gray-900">{{ t('end_message') }}</label>
                  <Tooltip>
                    <TooltipTrigger>
                      <CircleHelp class="size-4 text-gray-400" />
                    </TooltipTrigger>
                    <TooltipContent
                      variant="secondary"
                      showArrow
                      class="max-w-xs"
                    >
                      <p>{{ t('endMessageHint') }}</p>
                    </TooltipContent>
                  </Tooltip>
                </div>
                <TextAreaField
                  v-model="phoneNumber.end_message"
                  :value="phoneNumber.end_message ?? ''"
                  name="end_message"
                  :rules="yup.string()"
                  :placeholder="t('endMessagePlaceholder')"
                  rows="5"
                />
              </div>
            </AutoSavingForm>
          </div>
        </div>
      </div>
    </div>
  </TooltipProvider>
</template>
